import { Typography } from '@material-ui/core';
import { useDeepEqualValue } from '@superdispatch/hooks';
import {
  Color,
  ColorDark,
  ColorDynamic,
  Column,
  Columns,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useState } from 'react';
import { useShowSubscriptionCancelledDialog } from 'shared/errors/data/useShowSubscriptionCancelledDialog';
import { useShowTrialExpiredDialog } from 'shared/errors/data/useShowTrialExpiredDialog';
import { LoadOfferSidebarIcon } from 'shared/icons/LoadOfferSidebarIcon';
import { SidebarLogoIcon } from 'shared/icons/SidebarLogoIcon';
import { AppCarrierMenuLegacy } from 'shared/layout/sidebar/AppCarrierMenuLegacy';
import {
  AppSidebarMenuLegacy,
  SidebarMenuLinkLegacy,
  StyledBadge,
  StyledLinkLegacyIcon,
} from 'shared/layout/sidebar/AppSidebarMenuLegacy';
import { LoadOffersButton } from 'shared/modules/load-offers/LoadOffersButton';
import { useTMSLandingURL } from 'shared/routing/TMSLandingPath';
import styled from 'styled-components';
import { useCarrierMenuLegacy } from './useCarrierMenuLegacy';
import { useEdiVehiclesIntercomTour } from './useEdiVehiclesIntercomTour';
import { useIntercomTour } from 'shared/hooks/useIntercomTour';
import { useIntercomSurvey } from 'shared/hooks/useIntercomSurvey';
import { useSidebarItems } from './useSidebarItems';
import { useFlag } from 'shared/settings/FeatureToggles';
import { AppSidebarMenu } from './AppSidebarMenu';
import { SidebarTrialStatus } from './SidebarTrialStatus';
import { useSubscriptionTrialCountdown } from 'shared/modules/subscription/useSubscriptionTrialCountdown';
import { isProduction } from 'shared/constants/AppTypeConstants';

const Aside = styled.aside<{ isTrialExpired: boolean }>`
  height: calc(100vh - var(--navigation-header-banner-height, 0px));
  max-height: calc(100vh - var(--navigation-header-banner-height, 0px));
  max-width: 88px;
  align-self: flex-start;

  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  border-right: 1px solid ${ColorDynamic.Silver400};
  top: var(--navigation-header-banner-height, 0px);
  left: 0;
  z-index: ${({ isTrialExpired }) => (isTrialExpired ? 1400 : 1)};
  position: sticky;

  color: ${ColorDynamic.Silver500};
  background-color: ${({ theme }) =>
    theme.palette.type === 'dark' ? ColorDark.White : Color.Dark500};

  @media print {
    display: none;
  }
`;

const LogoContainer = styled.div(({ theme }) => {
  const mode = theme.palette.type;
  const color = mode === 'dark' ? ColorDark.White : Color.Dark500;
  return `
  display: flex;
  justify-content: center;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px 0;
  width: 100%;
  background-color: ${color};
  z-index: 2;
`;
});

const BoxContainer = styled(Box)`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export function LoadOffersLink() {
  const screen = useResponsiveValue('mobile', 'tablet', 'desktop');
  const isMobile = screen === 'mobile' || screen === 'tablet';
  const [isHovered, setIsHovered] = useState(false);
  const isSidebarChangesEnabled = useFlag('ctms_sidebar_changes');

  return (
    <LoadOffersButton
      render={({ loadOffersCount, onClick }) => {
        return (
          <SidebarMenuLinkLegacy
            onClick={onClick}
            onMouseOver={() => {
              setIsHovered(true);
            }}
            onMouseOut={() => {
              setIsHovered(false);
            }}
            data-dense={isSidebarChangesEnabled}
          >
            {isMobile ? (
              <Columns align="center">
                <Column width="fluid">
                  <Columns align="center" space="small">
                    <Column width="content">
                      <StyledLinkLegacyIcon
                        data-dense={isSidebarChangesEnabled}
                        as={LoadOfferSidebarIcon}
                      />
                    </Column>

                    <Column width="fluid">
                      <Typography variant="subtitle1">Offers</Typography>
                    </Column>
                  </Columns>
                </Column>
                <Column width="content">
                  {Boolean(loadOffersCount) && (
                    <BoxContainer backgroundColor="Red300">
                      <Typography align="center" variant="caption">
                        {loadOffersCount}
                      </Typography>
                    </BoxContainer>
                  )}
                </Column>
              </Columns>
            ) : (
              <>
                <StyledBadge
                  badgeContent={loadOffersCount}
                  color="error"
                  overlap="rectangular"
                  $highlighted={isHovered}
                >
                  <StyledLinkLegacyIcon
                    data-dense={isSidebarChangesEnabled}
                    as={LoadOfferSidebarIcon}
                  />
                </StyledBadge>
                Offers
              </>
            )}
          </SidebarMenuLinkLegacy>
        );
      }}
    />
  );
}

interface AppLayoutSidebarLegacyProps {
  localPaths?: {
    dashboard?: string;
    home?: string;
    loads?: string;
    contacts?: string;
    users?: string;
    drivers?: string;
    trips?: string;
    tracking?: string;
    loadboard?: string;
    ratings?: string;
    dispatcherProfile?: string;
    carrierProfile?: string;
    reports?: string;
    settings?: string;
    subscriptionDetails?: string;
    logout?: string;
    dispatcherManagement?: string;
    superCard?: string;
  };
  hasMenuBadge: boolean;
}

export function AppLayoutSidebarLegacy({
  localPaths: localPathsProp = {},
  hasMenuBadge,
}: AppLayoutSidebarLegacyProps) {
  const localPaths = useDeepEqualValue(localPathsProp);
  const isSidebarChangesEnabled = useFlag('ctms_sidebar_changes');
  const isReportingEnabled = useFlag('ctms_reporting');
  const isHomepageEnabled = useFlag('ctms_homepage');

  const landingURL = useTMSLandingURL();
  useEdiVehiclesIntercomTour();

  useIntercomTour({
    tourId: isProduction ? '680360' : '',
    isEnabled: isHomepageEnabled,
    seenStateKey: 'homepage_intercom_tour_seen_state',
  });

  useIntercomTour({
    tourId: isProduction ? '680480' : '',
    isEnabled: isReportingEnabled,
    seenStateKey: 'reports_page_intercom_tour_seen_state',
  });

  useIntercomSurvey({
    surveyId: isProduction ? '70575889' : '',
    isEnabled: isReportingEnabled,
    seenStateKey: 'carrier_tms_intercom_survey_seen_state',
  });

  const { isOpenTrialExpiredDialog } = useShowTrialExpiredDialog();
  const { isOpenSubscriptionCancelledDialog } =
    useShowSubscriptionCancelledDialog();
  const { ratingsLink, accountItems, carrierItems, dispatcherItems } =
    useCarrierMenuLegacy({
      localPaths,
    });
  const { homeLinks, offersLinks, primaryLinks, secondaryLinks } =
    useSidebarItems({
      localPaths,
    });

  const { isSubscriptionInTrial, trialDaysLeft } =
    useSubscriptionTrialCountdown({
      isEnabled: isSidebarChangesEnabled,
    });

  const isTrialExpired =
    !!isOpenTrialExpiredDialog || !!isOpenSubscriptionCancelledDialog;

  return (
    <Aside isTrialExpired={isTrialExpired}>
      <LogoContainer>
        <a href={landingURL} aria-label="Dashboard">
          <SidebarLogoIcon fontSize="large" htmlColor={Color.White} />
        </a>
      </LogoContainer>

      {isSidebarChangesEnabled ? (
        <AppSidebarMenu
          homeLinks={homeLinks}
          offersLinks={offersLinks}
          primaryLinks={primaryLinks}
          secondaryLinks={secondaryLinks}
        />
      ) : (
        <AppSidebarMenuLegacy
          homeLinks={homeLinks}
          offersLinks={offersLinks}
          primaryLinks={primaryLinks}
          secondaryLinks={secondaryLinks}
        />
      )}

      {isSubscriptionInTrial &&
        isSidebarChangesEnabled &&
        trialDaysLeft != null && <SidebarTrialStatus />}

      <AppCarrierMenuLegacy
        ratingsLink={ratingsLink}
        carrierItems={carrierItems}
        dispatcherItems={dispatcherItems}
        accountItems={accountItems}
        hasMenuBadge={hasMenuBadge}
      />
    </Aside>
  );
}
